import { onUnmounted, watch, type Ref } from 'vue'

/**
 * Custom property a top-anchored `<TToast />` stack offsets itself by.
 *
 * Read in TToast.vue's stylesheet with a `0px` fallback, so an app that never
 * publishes it gets the stack flush against PrimeVue's own 20px inset.
 */
export const TOAST_TOP_OFFSET_VAR = '--t-toast-top-offset'

/**
 * Publishes the height of an app's top chrome so a top-anchored `<TToast />`
 * clears it instead of covering it.
 *
 * Needed because the toast is `position: fixed` and Teleported to `<body>`,
 * while app chrome (aka chrome or app shell - refers to our banners and navbar; fixed, cached frame on webpage that stays mounted)
 * sits in normal flow with no fixed height -- thymebox stacks up to three variable-height banners above a nav bar that
 * re-lays-out at 1024px, and connect's AppBanner is the same shape. CSS alone cannot see any of that.
 * A ResizeObserver covers any height changes -- banner toggling, text
 * wrapping, breakpoint changes -- with no need to enumerate them.
 *
 * Call once from the app root, passing a ref to a single element wrapping all of
 * the chrome. Bottom-anchored stacks are measured from the bottom edge and can
 * never collide with top chrome, so they ignore this.
 * @param chrome - Element wrapping the banners and nav bar. May be undefined
 * while the chrome sits behind a `v-if`, in which case zero is published.
 */
export function useToastTopOffset(chrome: Ref<HTMLElement | undefined>) {
  let observer: ResizeObserver | undefined

  const publish = () =>
    document.documentElement.style.setProperty(
      TOAST_TOP_OFFSET_VAR,
      `${chrome.value?.getBoundingClientRect().height ?? 0}px`
    )

  watch(
    chrome,
    (element) => {
      observer?.disconnect()
      observer = undefined
      publish()
      if (!element) {
        return
      }
      observer = new ResizeObserver(publish)
      observer.observe(element)
    },
    { immediate: true }
  )

  onUnmounted(() => {
    observer?.disconnect()
    document.documentElement.style.removeProperty(TOAST_TOP_OFFSET_VAR)
  })
}
